/**
 * Export gate: decides whether an export may run (e.g. after a payment). The editor asks the gate
 * before rendering and reports the outcome afterwards; it knows nothing about payments.
 *
 * Planned: `StripeExportGate` — $1 per export via a Cloudflare Worker that creates a Stripe
 * Checkout session and, once paid, issues a signed single-use export token (see
 * docs/ARCHITECTURE.md, "Payments"). Swap it in at `billing/setup.ts`.
 */

export interface ExportRequest {
  projectId: string;
  projectName: string;
  width: number;
  height: number;
  durationSeconds: number;
}

export type ExportAuthorization =
  /** Proceed. `grant` is passed back on completion (e.g. a token to consume). */
  | { status: 'granted'; grant?: string }
  /** The user must leave the page (e.g. to a checkout); the export resumes when they return. */
  | { status: 'redirecting' }
  /** Not allowed; `reason` is user-presentable. */
  | { status: 'denied'; reason: string };

export interface ExportGate {
  /** Short, user-facing description of the cost, e.g. "Free" or "$1 per export". */
  readonly priceLabel: string;
  /** Called from the Export button's click (a user gesture). */
  authorize(request: ExportRequest): Promise<ExportAuthorization>;
  /** Called once the file was written successfully (e.g. to consume a single-use token). */
  completed?(request: ExportRequest, grant: string | undefined): Promise<void>;
  /** Called if the export failed or was cancelled after authorization (e.g. to restore a credit). */
  failed?(request: ExportRequest, grant: string | undefined): Promise<void>;
}

/** Exports are free (current behaviour). */
export class FreeExportGate implements ExportGate {
  readonly priceLabel = 'Free';

  async authorize(): Promise<ExportAuthorization> {
    return { status: 'granted' };
  }
}
